import { strIsNullOrEmpty } from '@fox/atoms';
import { formatCurrencyFromMinorUnits } from '@fox/finance';
import moment from 'moment';
import { forwardRef } from 'react';
import { Address } from '../../membership-form-template/contracts';
import { Receipt } from '../contracts';
import { getReceiptTotal } from '../shared';
import PoweredBySwiftFox from './powered-by-swiftfox.svg?url';

interface PrintableDocumentProps {
	title: string;
	receipt: Receipt;
	biller: {
		companyName?: string | null;
		companyAbn?: string | null;
		companyLogo?: string | null;
		companyAddress?: string | null;
		companyEmail?: string | null;
		companyPhone?: string | null;
	};
	billedTo: { name?: string | null; email?: string | null; address?: Address | null };
}

export enum PrintDocumentType {
	TaxReceipt = 'TaxReceipt'
}

const commonStyles = {
	flexBetweenStyle: {
		display: 'flex',
		justifyContent: 'space-between',
		marginBottom: '0.25rem'
	},
	noParagraphSpacing: {
		margin: 0
	},
	boldStyle: { fontWeight: 'bold', marginBottom: '0.3125rem' },
	tableCellStyle: (align: 'left' | 'right') => ({
		borderBottom: '0.0625rem solid #ddd',
		padding: '0.5rem',
		textAlign: align
	})
};

const DocumentHeader = ({ title, biller, billedTo, receipt }: PrintableDocumentProps) => {
	const receiptDate = moment(receipt.effectiveDate);

	const validDate = receiptDate.isValid() ? receiptDate.format('DD/MM/YYYY') : receipt.effectiveDate?.toString();
	return (
		<>
			<div style={commonStyles.flexBetweenStyle}>
				<div>
					<h1 style={{ fontSize: '2.125rem', fontWeight: 'normal', margin: 0 }}>{title.toUpperCase()}</h1>
					<div>
						<p>
							<b>Receipt Number:</b> {receipt.receiptNo}
						</p>
						<p>
							<b>Receipt Date:</b> {validDate}
						</p>
					</div>
				</div>
				<BillerLogo biller={biller} />
			</div>
			<div style={{ ...commonStyles.flexBetweenStyle, marginBottom: '1.5rem' }}>
				<Biller biller={biller} />
				<BilledTo billedTo={billedTo} />
			</div>
		</>
	);
};

const BillerLogo = ({ biller }: { biller: PrintableDocumentProps['biller'] }) => (
	<>
		{biller.companyLogo ? (
			<img src={biller.companyLogo} alt={`${biller.companyName ?? 'Company'} logo`} style={{ maxHeight: '6rem', maxWidth: '6rem' }} />
		) : null}
	</>
);

const Biller = ({ biller }: { biller: PrintableDocumentProps['biller'] }) => {
	const { companyAbn, companyAddress, companyEmail, companyName, companyPhone } = biller;
	return (
		<div>
			{companyName ? <p style={{ marginTop: 0, fontWeight: 'bold' }}>{companyName}</p> : null}
			{companyAddress ? <p style={commonStyles.noParagraphSpacing}>{companyAddress}</p> : null}
			{companyEmail ? (
				<p style={commonStyles.noParagraphSpacing}>
					<a href={`mailto:${companyEmail}`}>{companyEmail}</a>
				</p>
			) : null}
			{companyPhone ? <p style={commonStyles.noParagraphSpacing}>{companyPhone}</p> : null}
			{companyAbn ? <p style={commonStyles.noParagraphSpacing}>ABN: {companyAbn}</p> : null}
		</div>
	);
};

const BilledTo = ({ billedTo }: { billedTo: PrintableDocumentProps['billedTo'] }) => {
	const billerAddresses = billedTo.address;
	const addressParts = billerAddresses
		? [billerAddresses.address1, billerAddresses.locality, billerAddresses.state, billerAddresses.postcode].filter(
				part => !strIsNullOrEmpty(part)
		  )
		: [];
	const billerMainAddress = billerAddresses?.formatted ?? (addressParts.length ? addressParts.join(' ') : null);
	return (
		<>
			{billedTo ? (
				<div>
					<p style={{ marginTop: 0, fontWeight: 'bold' }}>Billed To:</p>
					<p style={commonStyles.noParagraphSpacing}>{billedTo.name}</p>
					{billedTo.email ? <p style={commonStyles.noParagraphSpacing}>{billedTo.email}</p> : null}
					{billerMainAddress ? <p style={commonStyles.noParagraphSpacing}>{billerMainAddress}</p> : null}
				</div>
			) : null}
		</>
	);
};

const PrintableDocument = forwardRef<HTMLDivElement, { children: React.ReactNode }>(({ children }, ref) => {
	return (
		<div
			ref={ref}
			style={{
				fontFamily: 'Arial, sans-serif',
				maxWidth: '50rem',
				// magical number for height to fit page with the padding and place Powered By to the very bottom of container
				minHeight: '93vh',
				margin: '0 auto',
				padding: '2rem 1.5rem',
				display: 'flex',
				flexDirection: 'column'
			}}
		>
			{children}
		</div>
	);
});

type PrintableReceiptProps = PrintableDocumentProps & {
	receipt: Receipt;
};

export const PrintableReceipt = forwardRef<
	HTMLDivElement,
	PrintableDocumentProps & {
		receipt: Receipt;
	}
>(({ title, biller, billedTo, receipt }: PrintableReceiptProps, ref) => {
	return (
		<div style={{ display: 'none' }}>
			<PrintableDocument ref={ref}>
				<DocumentHeader title={title} biller={biller} billedTo={billedTo} receipt={receipt} />
				{/* Line Items Table */}
				<table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1.25rem' }}>
					<thead>
						<tr>
							{['Item', 'Description', 'Amount'].map((heading, index) => (
								<th
									key={index}
									style={{
										backgroundColor: '#cccccc',
										borderBottom: '0.0625rem solid #ddd',
										padding: '0.625rem',
										textAlign: heading === 'Amount' ? 'right' : 'left',
										width: heading === 'Description' ? '60%' : '20%'
									}}
								>
									{heading}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{receipt.lineItems.map((item, idx) => (
							<tr key={`${item.id}_${idx}`}>
								<td style={commonStyles.tableCellStyle('left')}>{item.lineType.name}</td>
								<td style={commonStyles.tableCellStyle('left')}>{item.comment}</td>
								<td style={commonStyles.tableCellStyle('right')}>
									{formatCurrencyFromMinorUnits(item.amount, receipt.currency, { cutOffCents: false })}
								</td>
							</tr>
						))}
					</tbody>
				</table>

				{/* Summary */}
				<div style={{ textAlign: 'right', marginBottom: '1.25rem' }}>
					<p>
						<strong>
							Total:{' '}
							{formatCurrencyFromMinorUnits(getReceiptTotal(receipt), receipt.currency, {
								cutOffCents: false
							})}
						</strong>
					</p>
				</div>
				<img
					src={PoweredBySwiftFox}
					style={{
						marginTop: 'auto',
						alignSelf: 'flex-end',
						maxWidth: '12.5rem',
						maxHeight: '12.5rem'
					}}
				/>
			</PrintableDocument>
		</div>
	);
});
